import React, { useCallback, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { Line } from "react-chartjs-2";

// Регистрация модулей Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

/**
 * Props:
 *  - data: [{ ts: "2025-08-19T12:00:00", level: -23.4 }, ...]
 *  - xMin: Date | string (левая граница оси X)
 *  - xMax: Date | string (правая граница оси X)
 *  - timeUnit: "minute" | "second"
 *      сутки  → "minute"
 *      час/минута → "second"
 */
export default function LevelsChart({ data = [], xMin, xMax, timeUnit = "minute" }) {
  // точки
  const points = useMemo(() => {
    return (Array.isArray(data) ? data : [])
      .filter((p) => p && p.ts != null && Number.isFinite(Number(p.level)))
      .map((p) => ({ x: new Date(String(p.ts)), y: Number(p.level) }));
  }, [data]);

  const xMinDate = useMemo(() => (xMin ? new Date(xMin) : undefined), [xMin]);
  const xMaxDate = useMemo(() => (xMax ? new Date(xMax) : undefined), [xMax]);
  const spanSec =
    xMinDate && xMaxDate ? Math.max(1, Math.floor((xMaxDate - xMinDate) / 1000)) : 0;

  // Режимы
  const isDaily = spanSec >= 20 * 3600;                // ~ сутки
  const isMinute = timeUnit === "second" && spanSec <= 120; // ~ минута
  const isHour = timeUnit === "second" && !isMinute;        // ~ час

  // Форматтеры (24h)
  const fmtHM = useCallback((d) =>
    new Intl.DateTimeFormat("uk-UA", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(d), []);

  const fmtHMS = useCallback((d) =>
    new Intl.DateTimeFormat("uk-UA", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(d), []);

  const fmtYMDHMS = useCallback((d) => {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    const hms = fmtHMS(dt);
    return `${y}-${m}-${day} ${hms}`;
  }, [fmtHMS]);

  const chartData = useMemo(
    () => ({
      datasets: [
        {
          label: "Рівень звуку (dB)",
          data: points,
          borderColor: "rgb(0,110,69)",
          backgroundColor: "rgba(0,110,69,0.18)",
          pointRadius: 0,
          pointHitRadius: 6,
          borderWidth: 2,
          tension: 0.25,
        },
      ],
    }),
    [points]
  );

  const options = useMemo(() => {
    // Конфигурация тиков и сетки по режимам
    const cfg = isDaily
      ? {
          unit: "hour",          // сутки → только часы
          step: 1,
          fmtTick: (d) => `${String(new Date(d).getHours()).padStart(2, "0")}:00`,
          gridMajor: () => true, // каждый час
          tooltipFmt: (d) => fmtYMDHMS(d),
        }
      : isHour
      ? {
          unit: "minute",        // час → подпись раз в 15 минут
          step: 15,
          fmtTick: (d) => fmtHM(new Date(d)),
          gridMajor: (d) => new Date(d).getMinutes() === 0, // жирная линия на полном часе
          tooltipFmt: (d) => fmtYMDHMS(d),
        }
      : {
          unit: "second",        // минута → подпись раз в 10 секунд
          step: 10,
          fmtTick: (d) =>
            new Intl.DateTimeFormat("uk-UA", {
              minute: "2-digit",
              second: "2-digit",
              hour12: false,
            }).format(new Date(d)),
          gridMajor: (d) => new Date(d).getSeconds() % 30 === 0, // жирная каждые 30 сек
          tooltipFmt: (d) => fmtYMDHMS(d),
        };

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: {
          callbacks: {
            title: (items) => {
              const x = items?.[0]?.parsed?.x;
              return x ? cfg.tooltipFmt(x) : "";
            },
            label: (ctx) => `${ctx.parsed.y} dB`,
          },
        },
        datalabels: { display: false },
      },
      scales: {
        x: {
          type: "time",
          min: xMinDate ? xMinDate.getTime() : undefined,
          max: xMaxDate ? xMaxDate.getTime() : undefined,
          time: {
            unit: cfg.unit,
            stepSize: cfg.step,
            displayFormats: {
              hour: "HH:mm",
              minute: "HH:mm",
              second: "mm:ss",
            },
            tooltipFormat: "HH:mm:ss",
          },
          ticks: {
            autoSkip: false, // показываем ровно наш шаг
            maxRotation: 0,
            callback: (value) => cfg.fmtTick(Number(value)),
          },
          grid: {
            color: (ctx) =>
              cfg.gridMajor(ctx.tick.value)
                ? "rgba(0,0,0,0.28)"
                : "rgba(0,0,0,0.1)",
            lineWidth: (ctx) => (cfg.gridMajor(ctx.tick.value) ? 1.2 : 0.6),
            tickLength: 6,
          },
          title: { display: true, text: "Час" },
        },
        y: {
          title: { display: true, text: "Рівень (dB)" },
          ticks: { callback: (v) => `${v}` },
          grid: { display: true },
        },
      },
    };
    }, [xMinDate, xMaxDate, isDaily, isHour, fmtHM, fmtYMDHMS]);



  return (
    <div
      style={{
        width: "100%",
        height: 400,
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 2px 12px #0001",
        padding: 12,
      }}
    >
      <Line data={chartData} options={options} />
    </div>
  );
}
